import { omit } from 'lodash';
import { matchPath } from 'react-router-dom';
import { blockHasValue } from '@plone/volto/helpers';
import config from '@plone/volto/registry';

const ALLOWED_LIST_BLOCKS = ['contextNavigation', 'mpfServicosDestaque', 'mpfUnidadeResponsavel', 'mpfConteudosRelacionados', 'slate', 'tags'];

export function restrictToPath(match) {
  return ({ pathname }) => matchPath(pathname, match);
}

/**
 * Should a slot be rendered?
 *
 * Based on persistent slot data and slot definitions, this function answers
 * the slot availability question.
 */
export function isSlotAvailable({ slotName, pathname, slotData, slots }) {
  return [...(Object.values(slotData?.items?.[slotName]?.blocks || {}).filter((item) => blockHasValue({ pathname, ...item })) || []), ...slots[slotName]?.items?.filter((reg) => reg.available({ pathname, slotName, slotData, slots }))].length > 0;
}

/**
 * Does the slot contain a block of this type?
 *
 * Diferente de `isSlotAvailable`, que responde "esta lateral tem algo a
 * renderizar?", aqui a pergunta é "um componente específico está na
 * lateral?". Serve a quem tem um comportamento alternativo quando o bloco
 * NÃO foi posicionado ali — caso da Área responsável (RF03), que cai no
 * rodapé do conteúdo em vez de simplesmente sumir.
 *
 * `slotData` já chega com os blocos herdados de páginas ancestrais mesclados
 * (ver `Slots.get_data` em `je.slots.backend`), então um bloco herdado conta
 * como presente — que é o que o leitor da página vê.
 */
export function slotHasBlockType({ slotName, pathname, slotData, type }) {
  return Object.values(slotData?.items?.[slotName]?.blocks || {}).some((block) => block?.['@type'] === type && blockHasValue({ pathname, ...block }));
}

/**
 * Remove blocks of the list
 */
export function removeBlocksSlotsConfig(blocksConfig) {
  const copyBlocksConfig = {
    ...blocksConfig,
  };
  const filteredBlocks = Object.keys(copyBlocksConfig)
    .filter((key) => ALLOWED_LIST_BLOCKS.includes(key))
    .reduce((obj, key) => {
      obj[key] = copyBlocksConfig[key];
      return obj;
    }, {});
  return filteredBlocks;
}

/**
 * Set restricted to false for slot fills
 */
export function slotsBlocksConfig(blocksConfig) {
  const newBlocksConfig = removeBlocksSlotsConfig(blocksConfig);
  return Object.assign(
    {},
    ...Object.keys(newBlocksConfig).map((id) => ({
      [id]: {
        ...newBlocksConfig[id],
        restricted: newBlocksConfig[id].isSlotFill ? false : newBlocksConfig[id].isSlotFill,
        sidebarTab: 0,
      },
    })),
  );
}

/**
 * Placeholder blocks are annoying to deal with when they're inherited.
 * We remove the last placeholder blocks from provided form data
 *
 * @function cleanupLastPlaceholders
 * @param {Object} formData Form data with `blocks` and `blocks_layout`
 * @return {Object} formData without placeholder blocks at the end
 */
export function cleanupLastPlaceholders(formData) {
  const { blocks, blocks_layout } = formData;
  if (!blocks_layout?.items?.length) return formData;

  const remove = [];
  [...(blocks_layout.items || [])].reverse().find((uid) => {
    if (isPlaceholderBlock(blocks[uid])) {
      remove.push(uid);
      return false;
    } else {
      return true;
    }
  });

  return {
    ...formData,
    blocks_layout: {
      ...blocks_layout,
      items: blocks_layout.items.filter((uid) => remove.indexOf(uid) === -1),
    },
    blocks: omit(blocks, remove),
  };
}

/**
 * Returns true if the block data is an empty placeholder block
 * @function isPlaceholderBlock
 * @param {Object} blockData Block data
 * @return {Boolean} True if block data is an empty placeholder block
 */
export function isPlaceholderBlock(blockData) {
  const { settings } = config;
  return blockData?.['@type'] === settings.defaultBlockType && !blockHasValue(blockData);
}
